/**
 * Curve-specific edit operations — mapping between CurveData and the flat
 * edit-vertex array used by the edit mode selection and transform systems.
 *
 * Edit vertex layout for N control points (3N edit vertices, 9N floats):
 *   Index i*3 + 0 → point[i].co         (control point)
 *   Index i*3 + 1 → point[i].handleLeft  (left handle)
 *   Index i*3 + 2 → point[i].handleRight (right handle)
 */

import type { Vec3 } from "./curvemath.ts";
import {
  enforceHandleConstraintsBatch,
  setPointHandleType,
  subdivideCurveSegment,
} from "./curvemath.ts";
import type { CurveData, CurveHandleType, CurvePoint } from "./types.ts";

// ---------------------------------------------------------------------------
// Index mapping helpers
// ---------------------------------------------------------------------------

/** True if the edit vertex index corresponds to a control point (not a handle). */
export function isCurveControlPoint(editIdx: number): boolean {
  return editIdx % 3 === 0;
}

/** Get the curve point index from an edit vertex index. */
export function curvePointIndex(editIdx: number): number {
  return Math.floor(editIdx / 3);
}

/** Edit vertex index for a control point. */
export function editIndexForCo(pointIndex: number): number {
  return pointIndex * 3;
}

/** Edit vertex index for a left handle. */
export function editIndexForHandleLeft(pointIndex: number): number {
  return pointIndex * 3 + 1;
}

/** Edit vertex index for a right handle. */
export function editIndexForHandleRight(pointIndex: number): number {
  return pointIndex * 3 + 2;
}

// ---------------------------------------------------------------------------
// Build / apply edit vertices
// ---------------------------------------------------------------------------

/** Build a flat Float32Array of edit vertices from CurveData. */
export function buildCurveEditVertices(curveData: CurveData): Float32Array {
  const pts = curveData.points;
  const arr = new Float32Array(pts.length * 9); // 3 positions * 3 components each
  for (let i = 0; i < pts.length; i++) {
    const base = i * 9;
    arr[base] = pts[i].co[0];
    arr[base + 1] = pts[i].co[1];
    arr[base + 2] = pts[i].co[2];
    arr[base + 3] = pts[i].handleLeft[0];
    arr[base + 4] = pts[i].handleLeft[1];
    arr[base + 5] = pts[i].handleLeft[2];
    arr[base + 6] = pts[i].handleRight[0];
    arr[base + 7] = pts[i].handleRight[1];
    arr[base + 8] = pts[i].handleRight[2];
  }
  return arr;
}

/** Write flat edit vertices back into a deep-cloned CurveData. */
export function applyCurveEditVertices(
  curveData: CurveData,
  editVerts: Float32Array,
): CurveData {
  const points: CurvePoint[] = curveData.points.map((pt, i) => {
    const base = i * 9;
    return {
      ...pt,
      co: [editVerts[base], editVerts[base + 1], editVerts[base + 2]] as Vec3,
      handleLeft: [
        editVerts[base + 3],
        editVerts[base + 4],
        editVerts[base + 5],
      ] as Vec3,
      handleRight: [
        editVerts[base + 6],
        editVerts[base + 7],
        editVerts[base + 8],
      ] as Vec3,
    };
  });
  return { ...curveData, points };
}

// ---------------------------------------------------------------------------
// Subdivide selected curve segments
// ---------------------------------------------------------------------------

/**
 * Subdivide curve segments between adjacent selected control points.
 * Returns updated CurveData and the new selection (including inserted points).
 */
export function subdivideCurveSelection(
  curveData: CurveData,
  selectedVertices: Set<number>,
): { curveData: CurveData; newSelection: Set<number> } | null {
  const n = curveData.points.length;
  const segCount = curveData.cyclic ? n : n - 1;

  // Find segments where both endpoints are selected control points
  const selectedPoints = new Set<number>();
  for (const vi of selectedVertices) {
    if (isCurveControlPoint(vi)) {
      selectedPoints.add(curvePointIndex(vi));
    }
  }

  const segsToSplit: number[] = [];
  for (let seg = 0; seg < segCount; seg++) {
    const i0 = seg;
    const i1 = (seg + 1) % n;
    if (selectedPoints.has(i0) && selectedPoints.has(i1)) {
      segsToSplit.push(seg);
    }
  }

  if (segsToSplit.length === 0) return null;

  // Split from highest index to lowest to avoid index shifting
  const newPoints = [
    ...curveData.points.map((p) => ({
      ...p,
      co: [...p.co] as Vec3,
      handleLeft: [...p.handleLeft] as Vec3,
      handleRight: [...p.handleRight] as Vec3,
    })),
  ];
  const insertedIndices: number[] = [];

  for (let s = segsToSplit.length - 1; s >= 0; s--) {
    const segIdx = segsToSplit[s];
    const result = subdivideCurveSegment(newPoints, segIdx);

    // Update the left point's right handle
    newPoints[segIdx].handleRight = result.updatedLeftHandleRight;
    // Update the right point's left handle
    const rightIdx = (segIdx + 1) % newPoints.length;
    newPoints[rightIdx].handleLeft = result.updatedRightHandleLeft;
    // Insert the new point after segIdx
    newPoints.splice(segIdx + 1, 0, result.newPoint);
    insertedIndices.push(segIdx + 1);
  }

  // Build new selection: original selected points (remapped) + inserted points
  const newSelection = new Set<number>();
  for (const idx of insertedIndices) {
    newSelection.add(editIndexForCo(idx));
  }
  // Keep original selections (remapped through insertions)
  for (const pi of selectedPoints) {
    // Count how many insertions happened before this point
    let offset = 0;
    for (const ii of insertedIndices) {
      if (ii <= pi + offset) offset++;
    }
    newSelection.add(editIndexForCo(pi + offset));
  }

  return {
    curveData: { ...curveData, points: newPoints },
    newSelection,
  };
}

// ---------------------------------------------------------------------------
// Delete selected curve points
// ---------------------------------------------------------------------------

/**
 * Delete selected control points from the curve.
 * Minimum 2 points must remain.
 */
export function deleteCurvePoints(
  curveData: CurveData,
  selectedVertices: Set<number>,
): CurveData | null {
  const selectedPoints = new Set<number>();
  for (const vi of selectedVertices) {
    if (isCurveControlPoint(vi)) {
      selectedPoints.add(curvePointIndex(vi));
    }
  }

  const remaining = curveData.points.filter((_, i) => !selectedPoints.has(i));
  if (remaining.length < 2) return null;

  return { ...curveData, points: remaining };
}

// ---------------------------------------------------------------------------
// Extend curve from endpoint
// ---------------------------------------------------------------------------

/** Distance to offset the new point along the tangent. */
const EXTEND_OFFSET = 0.01;

/** Normalized direction from a control point toward a handle, with fallback. */
function tangentFromHandle(co: Vec3, handle: Vec3): Vec3 {
  const dx = handle[0] - co[0];
  const dy = handle[1] - co[1];
  const dz = handle[2] - co[2];
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-10) return [0, 0, 1]; // fallback: extend upward
  return [dx / len, dy / len, dz / len];
}

/**
 * Add a new control point extending from the first or last point.
 * Returns the updated CurveData and the edit vertex index of the new point.
 */
export function extendCurve(
  curveData: CurveData,
  selectedVertices: Set<number>,
): { curveData: CurveData; newPointEditIndex: number } | null {
  const n = curveData.points.length;

  // Find which endpoint is selected
  let extendFromEnd = false;
  let extendFromStart = false;

  for (const vi of selectedVertices) {
    if (!isCurveControlPoint(vi)) continue;
    const pi = curvePointIndex(vi);
    if (pi === 0) extendFromStart = true;
    if (pi === n - 1) extendFromEnd = true;
  }

  if (!extendFromEnd && !extendFromStart) return null;

  const newPoints = curveData.points.map((p) => ({
    ...p,
    co: [...p.co] as Vec3,
    handleLeft: [...p.handleLeft] as Vec3,
    handleRight: [...p.handleRight] as Vec3,
  }));

  // Place the new point near the endpoint along its outgoing tangent.
  // A small offset keeps the Bezier segment non-degenerate so the surface
  // generates correctly during the brief moment before the user drags.
  // The caller enters a translate command immediately after.
  if (extendFromEnd) {
    const last = newPoints[n - 1];
    const tangent = tangentFromHandle(last.co, last.handleRight);
    const newCo: Vec3 = [
      last.co[0] + tangent[0] * EXTEND_OFFSET,
      last.co[1] + tangent[1] * EXTEND_OFFSET,
      last.co[2] + tangent[2] * EXTEND_OFFSET,
    ];
    const hLen = EXTEND_OFFSET / 3;
    const newPoint: CurvePoint = {
      co: newCo,
      handleLeft: [
        newCo[0] - tangent[0] * hLen,
        newCo[1] - tangent[1] * hLen,
        newCo[2] - tangent[2] * hLen,
      ],
      handleRight: [
        newCo[0] + tangent[0] * hLen,
        newCo[1] + tangent[1] * hLen,
        newCo[2] + tangent[2] * hLen,
      ],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      radius: last.radius,
      tilt: last.tilt,
    };
    newPoints.push(newPoint);
    return {
      curveData: { ...curveData, points: newPoints },
      newPointEditIndex: editIndexForCo(newPoints.length - 1),
    };
  }

  // Extend from start — mirror logic along the incoming tangent
  const first = newPoints[0];
  const tangent = tangentFromHandle(first.co, first.handleLeft);
  const newCo: Vec3 = [
    first.co[0] + tangent[0] * EXTEND_OFFSET,
    first.co[1] + tangent[1] * EXTEND_OFFSET,
    first.co[2] + tangent[2] * EXTEND_OFFSET,
  ];
  const hLen = EXTEND_OFFSET / 3;
  const newPoint: CurvePoint = {
    co: newCo,
    handleLeft: [
      newCo[0] + tangent[0] * hLen,
      newCo[1] + tangent[1] * hLen,
      newCo[2] + tangent[2] * hLen,
    ],
    handleRight: [
      newCo[0] - tangent[0] * hLen,
      newCo[1] - tangent[1] * hLen,
      newCo[2] - tangent[2] * hLen,
    ],
    handleTypeLeft: "aligned",
    handleTypeRight: "aligned",
    radius: first.radius,
    tilt: first.tilt,
  };
  newPoints.unshift(newPoint);
  return {
    curveData: { ...curveData, points: newPoints },
    newPointEditIndex: editIndexForCo(0),
  };
}

// ---------------------------------------------------------------------------
// Set handle type for selected points
// ---------------------------------------------------------------------------

export function setCurveHandleType(
  curveData: CurveData,
  selectedVertices: Set<number>,
  type: CurveHandleType,
): CurveData {
  const newPoints = curveData.points.map((p) => ({
    ...p,
    co: [...p.co] as Vec3,
    handleLeft: [...p.handleLeft] as Vec3,
    handleRight: [...p.handleRight] as Vec3,
  }));

  for (const vi of selectedVertices) {
    if (!isCurveControlPoint(vi)) continue;
    const pi = curvePointIndex(vi);
    if (pi >= 0 && pi < newPoints.length) {
      setPointHandleType(newPoints, pi, type, curveData.cyclic);
    }
  }

  return { ...curveData, points: newPoints };
}

/**
 * Apply handle constraints after edit vertices have been transformed.
 * Call this during live transform to maintain handle type invariants.
 */
export function applyHandleConstraintsToEditVerts(
  editVerts: Float32Array,
  curveData: CurveData,
  movedVertices: Set<number>,
): void {
  // Build temporary points from edit verts
  const tempData = applyCurveEditVertices(curveData, editVerts);

  const indices: number[] = [];
  const components: Array<"co" | "handleLeft" | "handleRight"> = [];
  for (const vi of movedVertices) {
    const pi = curvePointIndex(vi);
    if (pi < 0 || pi >= tempData.points.length) continue;
    indices.push(pi);
    components.push(
      vi % 3 === 0 ? "co" : vi % 3 === 1 ? "handleLeft" : "handleRight",
    );
  }
  enforceHandleConstraintsBatch(
    tempData.points,
    indices,
    curveData.cyclic,
    components,
  );

  // Write back enforced positions
  for (let i = 0; i < tempData.points.length; i++) {
    const base = i * 9;
    const pt = tempData.points[i];
    editVerts[base] = pt.co[0];
    editVerts[base + 1] = pt.co[1];
    editVerts[base + 2] = pt.co[2];
    editVerts[base + 3] = pt.handleLeft[0];
    editVerts[base + 4] = pt.handleLeft[1];
    editVerts[base + 5] = pt.handleLeft[2];
    editVerts[base + 6] = pt.handleRight[0];
    editVerts[base + 7] = pt.handleRight[1];
    editVerts[base + 8] = pt.handleRight[2];
  }
}

/**
 * Get all control point edit indices for "Select All" on curves.
 */
export function allCurveControlPointIndices(curveData: CurveData): number[] {
  const indices: number[] = [];
  for (let i = 0; i < curveData.points.length; i++) {
    indices.push(editIndexForCo(i));
  }
  return indices;
}
